import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { OptionSelect } from "@/components/FormBits";
import { StatusPanel, TariffPanel } from "@/components/FacilityPanels";
import { LogItem } from "@/components/LogItem";
import { BookingRow } from "@/components/BookingRow";
import { useBookingDialog } from "@/context/BookingDialogContext";
import { api, formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/constants";

const LogPanel = ({ slug }) => {
  const [logs, setLogs] = useState([]);
  const [kind, setKind] = useState("segnalazione");
  const [message, setMessage] = useState("");
  const load = useCallback(() => api.get(`/facilities/${slug}/logs`).then((r) => setLogs(r.data)), [slug]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/facilities/${slug}/logs`, { kind, message });
      setMessage("");
      toast.success(kind === "segnalazione" ? "Segnalazione registrata" : "Nota aggiunta");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggle = async (log) => {
    await api.patch(`/logs/${log.id}`, { resolved: !log.resolved });
    load();
  };

  return (
    <section data-testid="log-panel" className="rounded-xl border border-slate-200 bg-white">
      <header className="border-b border-slate-100 px-5 py-4"><h2 className="font-serif text-2xl font-semibold">Note e segnalazioni</h2></header>
      <form onSubmit={add} className="space-y-3 border-b border-slate-100 p-5 bg-[#FBFAF7]">
        <div className="flex gap-3">
          <OptionSelect testId="log-kind-select" className="w-44" value={kind} onChange={setKind} options={{ segnalazione: "Segnalazione", nota: "Nota" }} />
          <Button type="submit" disabled={message.trim().length < 2} data-testid="log-submit-button" className="ml-auto bg-[#1E293B] hover:bg-slate-800">Registra</Button>
        </div>
        <Textarea data-testid="log-message-input" rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Es. Proiettore sala non funzionante, contattata ditta" />
      </form>
      {logs.length === 0
        ? <p className="px-5 py-8 text-center text-sm text-slate-500">Nessuna voce nel registro.</p>
        : <ul className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">{logs.map((l) => <LogItem key={l.id} log={l} onToggleResolved={toggle} />)}</ul>}
    </section>
  );
};

export default function FacilityDetail() {
  const { slug } = useParams();
  const { openNew, version } = useBookingDialog();
  const [facility, setFacility] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [logKey, setLogKey] = useState(0);

  useEffect(() => { api.get(`/facilities/${slug}`).then((r) => setFacility(r.data)); }, [slug]);
  useEffect(() => {
    api.get("/bookings", { params: { facility: slug, date_from: todayISO() } }).then((r) => setBookings(r.data));
  }, [slug, version]);

  if (!facility) return <p className="eyebrow">Caricamento…</p>;
  const onChange = (f) => { setFacility(f); setLogKey((k) => k + 1); };

  return (
    <div data-testid="facility-detail-page" className="space-y-8">
      <Link to="/strutture" data-testid="back-to-facilities" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#8B1E2B]"><ArrowLeft className="h-4 w-4" />Strutture</Link>
      <div className="relative h-56 sm:h-72 overflow-hidden rounded-2xl grain">
        <img src={facility.image_url} alt={facility.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B]/90 via-[#1E293B]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 z-10 p-6 sm:p-8 text-white">
          <p className="eyebrow !text-[#E8D3A3]">{facility.subtitle}</p>
          <h1 data-testid="facility-name" className="mt-1 font-serif text-4xl sm:text-5xl font-semibold">{facility.name}</h1>
          <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-200"><MapPin className="h-4 w-4" />{facility.location} · {facility.event_types}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        <div className="space-y-6">
          <StatusPanel key={`s-${facility.status_updated_at}`} facility={facility} onChange={onChange} />
          <TariffPanel facility={facility} onChange={onChange} />
        </div>
        <div className="space-y-6">
          <section data-testid="facility-bookings-panel" className="rounded-xl border border-slate-200 bg-white">
            <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="font-serif text-2xl font-semibold">Prossime concessioni</h2>
              <Button size="sm" onClick={() => openNew({ facility_slug: slug })} data-testid="facility-new-booking-button" className="bg-[#8B1E2B] hover:bg-[#701621] rounded-full"><Plus className="h-4 w-4 mr-1" />Prenota</Button>
            </header>
            {bookings.length === 0
              ? <p className="px-5 py-8 text-center text-sm text-slate-500">Nessuna concessione futura.</p>
              : <div className="divide-y divide-slate-100">{bookings.map((b) => <BookingRow key={b.id} b={b} />)}</div>}
          </section>
          <LogPanel key={logKey} slug={slug} />
        </div>
      </div>
    </div>
  );
}
